// Split & Push — when content is too large to index as a single document,
// this modal opens instead of a dead-end error. It divides the text into the
// fewest parts that each fit the chosen tier's limits, cutting only on clean
// paragraph/sentence boundaries (never mid-sentence/verse), and lets the user
// push each part as its own saved file — one at a time or all at once.
//
// Used by: StagingTab (paste push), Workbench (commit), FileRefinery (commit).
import { useEffect, useMemo, useState } from "react";
import { X, Scissors, PaperPlaneRight, CheckCircle, Warning, Anchor, BookOpen, Books } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Safe per-part size by tier. Backend caps (server.py): Tier1 300KB/doc (not
// chunked), Tier2 1MB + 500-chunk cap (~900K chars), Tier3 5MB + 500-chunk cap.
// We stay comfortably under whichever binds, with margin for multi-byte chars.
export const SAFE_CHARS_BY_TIER = { 1: 290000, 2: 850000, 3: 850000 };
export const MAX_SPLIT_CHARS = 10_000_000;

const TIER_META = {
  1: { label: "Tier 1 · Anchor", icon: Anchor },
  2: { label: "Tier 2 · Logic Core", icon: BookOpen },
  3: { label: "Tier 3 · Library", icon: Books },
};

// True when `content` is too big to push as one doc into `tier`.
export function needsSplit(content, tier) {
  const len = (content || "").length;
  return len > (SAFE_CHARS_BY_TIER[tier] || SAFE_CHARS_BY_TIER[3]);
}

// Split text into chunks each <= maxLen, preferring clean breaks: paragraph
// (\n\n) → sentence end (.!?) → any whitespace → hard cut as a last resort.
function splitOnBoundaries(text, maxLen) {
  const out = [];
  const n = text.length;
  let i = 0;
  while (i < n) {
    if (n - i <= maxLen) { out.push(text.slice(i)); break; }
    const hardEnd = i + maxLen;
    const floor = i + Math.floor(maxLen * 0.6); // keep parts reasonably full
    let cut = text.lastIndexOf("\n\n", hardEnd);
    if (cut < floor) {
      cut = -1;
      for (let j = hardEnd; j > floor; j--) {
        const ch = text[j];
        if ((ch === "." || ch === "!" || ch === "?") && /\s/.test(text[j + 1] || " ")) { cut = j + 1; break; }
      }
    }
    if (cut < floor) {
      cut = text.lastIndexOf(" ", hardEnd);
      if (cut < floor) cut = text.lastIndexOf("\n", hardEnd);
    }
    if (cut < floor) cut = hardEnd; // no boundary found — hard cut
    out.push(text.slice(i, cut));
    i = cut;
    while (i < n && /\s/.test(text[i])) i++; // trim leading whitespace of next part
  }
  return out.map((s) => s.trim()).filter(Boolean);
}

function partTitle(text, n) {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const first3 = words.slice(0, 3).join(" ");
  const last3 = words.slice(-3).join(" ");
  return `Part ${n}: "${first3} … ${last3}"`;
}

export default function SplitAndPushModal({
  open,
  content = "",
  defaultTitle = "",
  siloId,
  siloName = "this silo",
  initialTier = 2,
  sourceSurface = null,
  sourceOperation = null,
  onClose,
  onComplete,
}) {
  const [tier, setTier] = useState(initialTier);
  const [pushed, setPushed] = useState({});      // idx -> true
  const [errors, setErrors] = useState({});      // idx -> message
  const [pushingIdx, setPushingIdx] = useState(null); // number | "all" | null

  useEffect(() => {
    if (open) { setTier(initialTier); setPushed({}); setErrors({}); setPushingIdx(null); }
  }, [open, initialTier]);

  const text = (content || "").trim();
  const tooBig = text.length > MAX_SPLIT_CHARS;

  const parts = useMemo(() => {
    if (!open || tooBig) return [];
    const maxLen = SAFE_CHARS_BY_TIER[tier] || SAFE_CHARS_BY_TIER[3];
    const pieces = splitOnBoundaries(text, maxLen);
    return pieces.map((p, i) => ({ text: p, title: partTitle(p, i + 1), chars: p.length }));
  }, [open, text, tier, tooBig]);

  // Recompute clears progress whenever the part layout changes (tier switch).
  useEffect(() => { setPushed({}); setErrors({}); }, [tier]);

  if (!open) return null;

  const allDone = parts.length > 0 && parts.every((_, i) => pushed[i]);
  const busy = pushingIdx !== null;

  const pushOne = async (idx) => {
    const part = parts[idx];
    if (!part || !siloId) return;
    setPushingIdx(idx);
    try {
      await api.post(`/silos/${siloId}/docs/paste`, { title: part.title, content: part.text, tier, source_surface: sourceSurface, source_operation: sourceOperation });
      setPushed((p) => ({ ...p, [idx]: true }));
      setErrors((e) => { const n = { ...e }; delete n[idx]; return n; });
      toast.success(`Pushed Part ${idx + 1} to ${siloName} · Tier ${tier}.`, { id: `split-${idx}` });
    } catch (e) {
      const msg = e.response?.data?.detail || "Push failed.";
      setErrors((er) => ({ ...er, [idx]: msg }));
      toast.error(msg, { id: `split-err-${idx}` });
    } finally {
      setPushingIdx(null);
    }
  };

  const pushAll = async () => {
    if (!siloId) return;
    setPushingIdx("all");
    let okCount = 0;
    for (let i = 0; i < parts.length; i++) {
      if (pushed[i]) { okCount++; continue; }
      try {
        await api.post(`/silos/${siloId}/docs/paste`, { title: parts[i].title, content: parts[i].text, tier, source_surface: sourceSurface, source_operation: sourceOperation });
        setPushed((p) => ({ ...p, [i]: true }));
        setErrors((e) => { const n = { ...e }; delete n[i]; return n; });
        okCount++;
      } catch (e) {
        setErrors((er) => ({ ...er, [i]: e.response?.data?.detail || "Push failed." }));
      }
    }
    setPushingIdx(null);
    if (okCount === parts.length) toast.success(`All ${parts.length} parts pushed to ${siloName}.`, { id: "split-all" });
    else toast.error(`Pushed ${okCount} of ${parts.length}. Check the parts marked in red.`, { id: "split-all" });
  };

  const finish = () => { onComplete?.(); onClose?.(); };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="split-push-modal">
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-white border hairline rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[88vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b hairline shrink-0">
          <div className="flex items-center gap-2">
            <Scissors size={20} weight="duotone" className="text-[#4A5D4E]" />
            <div>
              <div className="font-serif-display text-lg leading-none text-[#1C1B1A]">Split &amp; Push</div>
              <div className="text-xs text-[#6B6A68] mt-1">Too big for one file — I'll cut it into clean parts you can push.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={busy}
            className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-40" data-testid="split-push-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {tooBig ? (
            <div className="flex gap-2 items-start text-sm text-[#C16E5A] bg-[#FDF1ED] border border-[#F2D0C5] rounded-md px-4 py-3" data-testid="split-too-big">
              <Warning size={16} weight="duotone" className="mt-0.5 shrink-0" />
              <div>This is <strong>{text.length.toLocaleString()}</strong> characters — over the {MAX_SPLIT_CHARS.toLocaleString()} limit even for splitting. Trim it down first, then try again.</div>
            </div>
          ) : (
            <>
              <div className="text-sm text-[#1C1B1A]">
                This is <span className="font-mono">{text.length.toLocaleString()}</span> characters.
                I'll split it into <strong>{parts.length} part{parts.length === 1 ? "" : "s"}</strong> so each one fits cleanly.
              </div>

              {/* Tier selector */}
              <div>
                <div className="block text-sm text-[#6B6A68] mb-2">Push all parts into</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[1, 2, 3].map((t) => {
                    const M = TIER_META[t]; const Icon = M.icon; const active = tier === t;
                    return (
                      <button key={t} type="button" onClick={() => setTier(t)} disabled={busy}
                        data-testid={`split-tier-${t}`}
                        className={`flex items-center gap-2 p-3 rounded-md border text-left text-sm transition-colors disabled:opacity-50 ${active ? "border-[#4A5D4E] bg-[#F0F1EE] text-[#1C1B1A]" : "hairline hover:border-[#4A5D4E]/50 text-[#6B6A68]"}`}>
                        <Icon size={16} weight="duotone" /> {M.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Parts list */}
              <div className="space-y-2">
                {parts.map((p, i) => {
                  const done = !!pushed[i];
                  const err = errors[i];
                  return (
                    <div key={i} data-testid={`split-part-${i}`}
                      className={`rounded-md border px-3 py-2.5 ${done ? "border-[#4A5D4E]/40 bg-[#F2F5F1]" : err ? "border-[#F2D0C5] bg-[#FDF1ED]" : "hairline bg-white"}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-[#1C1B1A] font-medium truncate">{p.title}</div>
                          <div className="text-[11px] text-[#6B6A68] font-mono">{p.chars.toLocaleString()} chars</div>
                        </div>
                        {done ? (
                          <span className="flex items-center gap-1 text-xs text-[#4A5D4E] shrink-0" data-testid={`split-part-done-${i}`}>
                            <CheckCircle size={16} weight="fill" /> Saved
                          </span>
                        ) : (
                          <button type="button" onClick={() => pushOne(i)} disabled={busy}
                            data-testid={`split-push-part-${i}`}
                            className="shrink-0 px-3 py-1.5 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] text-xs disabled:opacity-50 flex items-center gap-1">
                            <PaperPlaneRight size={12} weight="duotone" /> {pushingIdx === i ? "Pushing…" : `Push Part ${i + 1}`}
                          </button>
                        )}
                      </div>
                      <div className="text-[11px] text-[#6B6A68] mt-1.5 line-clamp-2 italic">{p.text.slice(0, 140)}{p.text.length > 140 ? "…" : ""}</div>
                      {err && <div className="text-[11px] text-[#C16E5A] mt-1.5" data-testid={`split-part-err-${i}`}>{err}</div>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!tooBig && (
          <div className="px-6 py-3 bg-[#FDFCF8] border-t hairline flex justify-between items-center gap-2 shrink-0">
            <div className="text-xs text-[#6B6A68]">
              {Object.keys(pushed).length} of {parts.length} pushed
            </div>
            <div className="flex gap-2">
              {allDone ? (
                <button type="button" onClick={finish} data-testid="split-done-btn"
                  className="px-5 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] text-sm flex items-center gap-1">
                  <CheckCircle size={14} weight="fill" /> Done
                </button>
              ) : (
                <>
                  <button type="button" onClick={onClose} disabled={busy}
                    className="px-4 py-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] text-sm disabled:opacity-40">Cancel</button>
                  <button type="button" onClick={pushAll} disabled={busy}
                    data-testid="split-push-all-btn"
                    className="px-5 py-2 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] text-sm disabled:opacity-50 flex items-center gap-1">
                    <PaperPlaneRight size={14} weight="duotone" /> {pushingIdx === "all" ? "Pushing all…" : "Push all parts"}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
